// src/pages/services/WebDevelopmentPage.jsx
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import webAnim from "../../assets/lottie/web.json";

export default function WebDevelopmentPage() {
  return (
    <div className="w-full bg-transparent text-black pt-12 px-6 md:px-20 pb-24">

      {/* ================= HERO ================= */}
      <section className="mx-auto flex max-w-7xl flex-col items-center gap-10 text-center">

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="hero-heading mb-6">
            Web Development
          </h1>

          <p className="hero-copy">
            We build fast, scalable and production-ready web applications —
            designed to perform reliably and scale with your business.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <Lottie
            animationData={webAnim}
            loop
            style={{ width: "85%", maxWidth: "420px" }}
          />
        </motion.div>

      </section>

      {/* ================= WHAT WE DELIVER ================= */}
      <section className="mx-auto mt-28 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          What We Deliver
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            "High-performance web applications",
            "Scalable frontend architecture",
            "Responsive & accessible UI",
            "Backend & API integrations",
            "SEO & performance optimization",
            "Admin dashboards & tools",
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="
                glass-card rounded-[22px] p-6
              "
            >
              <h3 className="text-[16px] font-medium">{item}</h3>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          Our Process
        </h2>

        <div className="space-y-14">
          {[
            ["01", "Strategy & Requirements", "Understanding business goals and product scope."],
            ["02", "UX Structure", "Planning layouts, navigation, and user flows."],
            ["03", "UI Implementation", "Designing a clean and consistent interface."],
            ["04", "Development", "Building scalable and maintainable frontend systems."],
            ["05", "Testing & Optimization", "Ensuring speed, security, and reliability."],
            ["06", "Deployment & Support", "Production deployment and ongoing support."],
          ].map(([step, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card flex gap-6 items-start rounded-[22px] p-5"
            >
              <h3 className="text-4xl font-semibold text-black">{step}</h3>
              <div>
                <h4 className="text-xl font-semibold mb-1">{title}</h4>
                <p className="text-black/60 text-[15px] leading-[1.6]">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= WHAT WE NEED ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          What We Need From You
        </h2>

        <ul className="mx-auto max-w-3xl space-y-4 text-center text-[16px] text-neutral-700">
          <li>• Business goals & vision</li>
          <li>• Target audience</li>
          <li>• Feature requirements</li>
          <li>• Brand references (if any)</li>
        </ul>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          Let’s build your website
        </h2>

        <a
          href="/contact"
          className="inline-block px-8 py-3 rounded-full bg-black text-white font-medium hover:bg-opacity-80 transition"
        >
          Contact Us
        </a>
      </section>

    </div>
  );
}